import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Checkbox, CodeField, Dialog, Drawer, Inline, PasswordInput, Stack, t, Text, TextField } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../../lib/api';
import { generateSecret, sealForGithub } from '../../lib/seal';
import { parseEnvImport } from '../../lib/envImport';

type Row = maintenance.components['schemas']['InstallationBatchPreview']['applications'][number];
type Variable = maintenance.components['schemas']['BatchVariable'];

/**
 * D105: the variables of an application of the repository, before its installation. The non-secret values are kept in
 * the assistant and written to the catalogue with the installation; the secrets are sealed in this browser with the
 * public key of the application's GitHub environment (created for it) and written there at « Enregistrer » (§ 7.2).
 * JSON (or .env) in, JSON out for a backup — the secrets typed here only when asked.
 */
export function BatchVariablesDrawer({ row, target, values, onValues, typedSecrets, onSecretsSaved, onStepUp, onClose }: {
  row: Row | null;
  target: { repository: string; ref: string; environmentType: string };
  /** The non-secret values typed so far for this application. */
  values: Record<string, string>;
  onValues: (values: Record<string, string>) => void;
  /** The secrets typed in this page for this application (for the export only: never stored). */
  typedSecrets: Record<string, string>;
  onSecretsSaved: (secrets: Record<string, string>) => void;
  onStepUp: () => void;
  onClose: () => void;
}) {
  const [plain, setPlain] = useState<Record<string, string>>(values);
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState('');
  const [ignored, setIgnored] = useState<string[]>([]);
  const [withSecrets, setWithSecrets] = useState(false);
  useEffect(() => { setPlain(values); setSecrets({}); setFailure(null); setIgnored([]); }, [row?.deployPath]);
  if (!row) return null;
  const items = row.variables.items.filter(v => !v.build);
  const build = row.variables.items.filter(v => v.build);
  const valueOf = (v: Variable) => plain[v.name] ?? v.value ?? '';
  const missing = (v: Variable) => v.required && (v.secret ? !secrets[v.name] && v.present === false : !valueOf(v).trim());
  const stillMissing = items.filter(missing).length;

  const parsed = parseEnvImport(importText);
  const applyImport = () => {
    const nextPlain = { ...plain };
    const nextSecrets = { ...secrets };
    const unknown: string[] = [];
    for (const { name, value } of parsed.variables) {
      const v = items.find(x => x.name === name);
      if (!v || (v.secret && v.immutable && v.present)) { unknown.push(name); continue; }
      if (v.secret) nextSecrets[name] = value; else nextPlain[name] = value;
    }
    setPlain(nextPlain);
    setSecrets(nextSecrets);
    setIgnored(unknown);
    setImporting(false);
    setImportText('');
  };

  const exportJson = () => {
    const out: Record<string, string> = {};
    for (const v of items) {
      if (!v.secret) out[v.name] = valueOf(v);
      else if (withSecrets && (secrets[v.name] ?? typedSecrets[v.name])) out[v.name] = secrets[v.name] ?? typedSecrets[v.name]!;
    }
    const url = URL.createObjectURL(new Blob([`${JSON.stringify(out, null, 2)}\n`], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${row.name}-${target.environmentType}.variables.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const save = async () => {
    setSending(true);
    setFailure(null);
    try {
      const typed = Object.entries(secrets).filter(([, v]) => v);
      if (typed.length) {
        const body = { ...target, deployPath: row.deployPath };
        const key = unwrap(await api.maintenance.POST('/installations/batch/secrets/key', { body }));
        for (const [name, value] of typed) {
          const encryptedValue = await sealForGithub(value, key.key);
          unwrap(await api.maintenance.PUT('/installations/batch/secrets/{name}', { params: { path: { name } }, body: { ...body, encryptedValue, keyId: key.keyId } }));
        }
        onSecretsSaved(Object.fromEntries(typed));
      }
      // Only what differs from env.example's value is kept (an untouched default needs no catalogue entry).
      onValues(Object.fromEntries(Object.entries(plain).filter(([name, v]) => v !== (row.variables.items.find(x => x.name === name)?.example ?? ''))));
      onClose();
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') onStepUp();
      else if (e instanceof ApiFailure) setFailure(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Drawer
        open
        size="xl"
        onClose={onClose}
        eyebrow={t('install.variables.eyebrow')}
        title={t('install.variables.drawerTitle', { name: row.name })}
        subtitle={t('install.variables.drawerSubtitle', { environment: row.variables.environment, path: `${row.deployPath}/env.example` })}
        headerActions={(
          <Inline gap={8}>
            <Button variant="secondary" size="sm" icon="upload" onClick={() => setImporting(true)}>{t('install.variables.import')}</Button>
            <Button variant="secondary" size="sm" icon="download" onClick={exportJson}>{t('install.variables.export')}</Button>
          </Inline>
        )}
        footer={(
          <Inline gap={8} align="center" justify="between">
            <Text variant="body-sm" tone={stillMissing ? 'neg' : 'mute'}>{stillMissing ? t('install.variables.stillMissing', { n: stillMissing }) : t('install.variables.complete')}</Text>
            <Inline gap={8}>
              <Button variant="secondary" onClick={onClose}>{t('install.variables.cancel')}</Button>
              <Button icon="save" disabled={sending} onClick={() => void save()}>{t('install.variables.save')}</Button>
            </Inline>
          </Inline>
        )}
      >
        <Stack gap={16}>
          {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>}
          {!row.variables.secretsReadable && <Alert tone="warn" title={t('environment.variables.unreadableTitle')}>{t('environment.variables.unreadableBody')}</Alert>}
          {ignored.length > 0 && <Alert tone="warn" title={t('install.variables.ignored', { names: ignored.join(', ') })} />}
          <Text variant="body-sm" tone="mute">{t('install.variables.help', { environment: row.variables.environment })}</Text>
          <Checkbox label={t('install.variables.exportSecrets')} checked={withSecrets} onChange={setWithSecrets} />
          {withSecrets && <Alert tone="warn" title={t('install.variables.exportSecretsWarn')} />}
          {items.length === 0 && <Text tone="mute">{t('install.variables.none')}</Text>}
          {items.map(v => (v.secret ? (
            <Stack key={v.name} gap={8}>
              <PasswordInput id={`var-${v.name}`} label={v.name} value={secrets[v.name] ?? ''} autoComplete="new-password" mono required={v.required}
                disabled={v.immutable && Boolean(v.present)}
                placeholder={v.present ? t('install.variables.secretPresent') : undefined}
                hint={[v.description, v.immutable ? t('install.variables.immutable') : null].filter(Boolean).join(' · ') || undefined}
                error={missing(v) ? t('install.variables.required') : undefined}
                onChange={e => setSecrets({ ...secrets, [v.name]: e.target.value })} />
              <Inline gap={8} align="center">
                <Badge tone="brand" size="sm">{t('environment.variables.secret')}</Badge>
                {v.present && <Badge tone="pos" size="sm">{t('install.variables.present')}</Badge>}
                {v.generate && !(v.immutable && v.present) && (
                  <Button variant="secondary" size="sm" icon="casino" onClick={() => setSecrets({ ...secrets, [v.name]: generateSecret(v.generate!) })}>{t('environment.variables.generate', { n: v.generate })}</Button>
                )}
              </Inline>
            </Stack>
          ) : (
            <TextField key={v.name} id={`var-${v.name}`} label={v.name} value={valueOf(v)} mono required={v.required}
              hint={[v.description, v.example ? t('install.variables.default', { value: v.example }) : null].filter(Boolean).join(' · ') || undefined}
              error={missing(v) ? t('install.variables.required') : undefined}
              onChange={e => setPlain({ ...plain, [v.name]: e.target.value })} />
          )))}
          {build.length > 0 && <Text variant="body-sm" tone="mute">{t('install.variables.build', { names: build.map(v => v.name).join(', ') })}</Text>}
        </Stack>
      </Drawer>
      <Dialog
        open={importing}
        size="lg"
        onClose={() => setImporting(false)}
        title={t('install.variables.importTitle', { name: row.name })}
        description={t('install.variables.importBody')}
        confirmLabel={t('install.variables.importConfirm', { n: parsed.variables.length })}
        confirmDisabled={parsed.variables.length === 0}
        onConfirm={applyImport}
      >
        {/* DSM CodeField: what is pasted as code (JSON or .env lines); no « Copier », the paste may hold secrets. */}
        <CodeField label={t('environment.variables.import.paste')} value={importText} onChange={(v: string) => setImportText(v)} rows={10}
          language={importText.trimStart().startsWith('{') ? 'JSON' : '.env'} copyable={false}
          placeholder={t('environment.variables.import.placeholder')}
          error={parsed.errors.includes('json') ? t('environment.variables.import.json') : parsed.errors.length ? t('environment.variables.import.unread', { what: parsed.errors.join(', ') }) : undefined} />
      </Dialog>
    </>
  );
}
